import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { RegApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Field, PageHeader } from "../../components/common";
import { QrScanner } from "../../components/QrScanner";
import { dateTime } from "../../lib/format";

const ERR = "rounded-xl bg-amber-50 p-3 text-sm text-amber-900";
const REQUIRED: [string, string][] = [["package_qr", "package QR"], ["card_serial", "card serial"], ["name", "full name"], ["line", "street address"],
  ["barangay", "barangay"], ["city", "city"], ["province", "province"], ["date_of_birth", "date of birth"], ["phone", "mobile number"],
  ["ec_name", "emergency contact person"], ["ec_phone", "emergency contact number"]];

export default function VerifyAccount() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState<Record<string, string>>({ package_qr: "", card_serial: "", name: "", line: "", barangay: "", city: "", province: "Cebu",
    postal_code: "", date_of_birth: "", phone: "", ec_name: "", ec_phone: "" });
  const [done, setDone] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const missing = REQUIRED.filter(([k]) => !f[k].trim()).map(([, l]) => l);
    if (missing.length) { setErr(`Please fill in: ${missing.join(", ")}`); return; }
    setErr(""); setBusy(true);
    try {
      const r = await RegApi.activate({ package_qr: f.package_qr, card_serial: f.card_serial, name: f.name, date_of_birth: f.date_of_birth, phone: f.phone,
        address: { line: f.line, barangay: f.barangay, city: f.city, province: f.province, postal_code: f.postal_code },
        emergency_contact: { name: f.ec_name, phone: f.ec_phone } });
      setUser(r.user); setDone(r.membership); qc.invalidateQueries({ queryKey: ["membership"] });
    } catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };

  if (done || user?.account_verified !== false) {
    return (
      <div className="fp-card space-y-3 text-center" data-testid="verify-success">
        <CheckCircle2 size={52} className="mx-auto text-[#036248]" />
        <p className="text-xl font-bold text-[#003F32]">Your account is verified</p>
        {done && <p className="text-sm text-[#036248]">{done.plan_name} membership active until {dateTime(done.ends_at)} · {done.free_rides} free rides{done.insurance ? ` · ${done.insurance}` : ""}.</p>}
        <Link to="/patient" className="fp-btn-primary h-12 w-full" data-testid="verify-home-button">Go to home</Link>
      </div>
    );
  }

  const input = (k: string, label: string, extra: any = {}) => (
    <Field label={label} hint={extra.hint}><input className={`fp-input ${extra.mono ? "font-mono uppercase" : ""}`} type={extra.type || "text"} value={f[k]} onChange={set(k)} data-testid={`verify-${k.replace(/_/g, "-")}-input`} /></Field>
  );
  return (
    <div className="space-y-5">
      <PageHeader title="Verify your account" subtitle="Optional — activates your membership card, free rides and insurance." />
      <section className="fp-card space-y-4" data-testid="verify-card-section">
        <p className="fp-label">1 · Package QR</p>
        <QrScanner testId="verify-package-scanner" label="Scan package QR" onScan={(t) => setF((x) => ({ ...x, package_qr: t }))} />
        {input("package_qr", "Or enter package code", { mono: true })}
        <p className="fp-label pt-2">2 · Card serial number</p>
        <QrScanner testId="verify-card-scanner" label="Scan card serial" onScan={(t) => setF((x) => ({ ...x, card_serial: t }))} />
        {input("card_serial", "Or enter card serial number", { mono: true })}
      </section>
      <section className="fp-card space-y-4" data-testid="verify-form">
        <p className="fp-label">3 · Your details</p>
        {input("name", "Full name")}
        {input("line", "House / Unit, Street")}
        <div className="grid grid-cols-2 gap-3">
          {input("barangay", "Barangay")}{input("city", "City / Municipality")}{input("province", "Province")}{input("postal_code", "ZIP code")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {input("date_of_birth", "Date of birth", { type: "date" })}{input("phone", "Mobile number", { type: "tel", hint: "e.g. 0917 123 4567" })}
        </div>
        <p className="fp-label pt-1">Emergency contact</p>
        <div className="grid grid-cols-2 gap-3">{input("ec_name", "Contact person")}{input("ec_phone", "Contact number", { type: "tel" })}</div>
      </section>
      {err && <p role="alert" className={ERR} data-testid="verify-error">{err}</p>}
      <button type="button" className="fp-btn h-12 w-full bg-[#FFC400] text-[#003F32] hover:bg-[#F4DF8C]" disabled={busy} onClick={submit} data-testid="verify-submit-button">
        {busy && <Loader2 size={16} className="animate-spin" />} Activate my membership
      </button>
    </div>
  );
}
